import React, {useEffect, useRef} from 'react';
import ReactPlayer from "react-player";
import {useAtom} from "jotai";
import {initializeState, initializeUrlState, reactPlayerAtom} from "../../../states/initialize";
import {playerState} from "../../../states/player";
import {timestamp2seconds} from "../../../helper/helper";
import {AIcaptionListState, captionItemState, captionListState, latestSavedCaptionAtom} from "../../../states/caption";
import {PeaksInstance, Segment} from 'peaks.js';
import {setExport2txt} from '../../../helper/axiosPost'
import {saveState} from '../../../states/action';

// import {CaptionListProps} from "../types/caption";

declare global {
  var peaksInstance: PeaksInstance | undefined
  var latestClickedTime: number | undefined
  var previousSegment: Segment | undefined
  var currentTime: number | undefined
  var latestSeekedTime: number | undefined

}

/**
 플레이어

 * @param data1 설명
 * @return {data1} 반환
 * @throws ValidException 예외 발생
 * @author plaq99@rumpus.co.kr
 * @date 03-20-2023
 */

export default function Player() {

  const [{
    playOn,
    muteOn,
    intervalOn,
    intervalStartTime,
    intervalEndTime,
    seekOnTime,
    currentVolume,
    currentTime,
    currentSpeed
  }, setPlayer] = useAtom(playerState);

  const reactPlayer = useRef<ReactPlayer>(null);
  const [reactPlayerGlobal, setReactPlayerGlobal] = useAtom(reactPlayerAtom)
  const [{uploadCaptionUrl, setCaptionUrl}] = useAtom(initializeUrlState)
  const [{video_url, end_time, duration}] = useAtom(initializeState);
  const [{needSaveOn}, setSave] = useAtom(saveState);
  const [captionList, setCaptionList] = useAtom(captionListState);
  const [AICaptionList] = useAtom(AIcaptionListState);
  const [latestSavedCaption, setLatestSavedCaption] = useAtom(latestSavedCaptionAtom);
  // const latestSavedCaptionList = useRef<CaptionListProps[] | null>(null)
  const [{intervalCaptionOn}, setCaptionItem] = useAtom(captionItemState);
  const [{job_id, user_id}] = useAtom(initializeState);
  const jobIdRef = useRef("")
  const userIdRef = useRef("")

  jobIdRef.current = job_id
  userIdRef.current = user_id

  function checkObjectsEqual(obj1: any, obj2: any) {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (let key of keys1) {
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }

    return true;
  }

  useEffect(function saveCaptionIntervally() {

    const _5MINUTES = 1000 * 60 * 5

    const timerId = setInterval(() => {
      // stale closure 회피
      setCaptionList((prevCaptionList) => {

        if (latestSavedCaption.current == null) {
          alert('latestSavedCaption이 없음. getCaptionData를 확인할 것')
          return prevCaptionList

        } else {
          const isChanged = latestSavedCaption.current.some((item, index) => {
            return !checkObjectsEqual(item, prevCaptionList[index])
          })

          if (isChanged) {

            setExport2txt(uploadCaptionUrl, uploadCaptionUrl, jobIdRef.current, userIdRef.current, prevCaptionList,AICaptionList, setSave)
            latestSavedCaption.current = structuredClone(prevCaptionList)
          } else {
            // alert('5분 사이에 변경되지 않았음')
          }
        }
        return prevCaptionList
      })

    }, _5MINUTES)

    return () => clearInterval(timerId);

  }, []);

  useEffect(() => {
    setReactPlayerGlobal(reactPlayer)
  }, []);


  /* 영상 재생시간 (duration) 수집 */
  useEffect(() => {

    setTimeout(() => {
      setPlayer((prev) => ({...prev, duration: duration }));
    }, 300)


  }, [end_time]);



  /* 현재 재생 시간에 따른 현재 자막 아이디 설정 (for 시간에 따른 자막 따라가기) */
  useEffect(() => {
    const captionItem = captionList.find((c) => currentTime >= timestamp2seconds(c.time_ini) && currentTime < timestamp2seconds(c.time_end));
    if (captionItem) {
      setCaptionItem((prev) => ({...prev, currentCaptionId: captionItem.seq, captionSelectedOn: false}));
    }

    if (intervalOn || intervalCaptionOn) {
      if (currentTime >= timestamp2seconds(intervalEndTime)) {
        reactPlayer.current?.seekTo(timestamp2seconds(intervalStartTime));
      }
    }

  }, [currentTime]);

  /* 재생 컨트롤바의 시간과 플레이어 시간 맞추기 */
  useEffect(() => {
    //0과 1사이일땐, 분수 type으로 넘김
    //https://github.com/cookpete/react-player#instance-methods
    currentTime < 1 && currentTime !== 0
    ?
    reactPlayer.current?.seekTo((currentTime / 100), "fraction")
    :
    reactPlayer.current?.seekTo(currentTime)

  }, [seekOnTime]);

  /* 반복 시작 */
  useEffect(() => {
    reactPlayer.current?.seekTo(timestamp2seconds(intervalStartTime));
    intervalOn && setPlayer((prev: any) => ({...prev, playOn: true}))
  }, [intervalOn]);

  /* 반복 종료 */
  useEffect(() => {
    reactPlayer.current?.seekTo(currentTime);
    !intervalOn && setPlayer((prev: any) => ({...prev, playOn: false}))
  }, [!intervalOn]);


  return (
    <>
      <ReactPlayer
        // muted={muteOn}
        ref={reactPlayer}
        url={video_url}
        muted={true}
        playing={playOn}
        playsinline={true}
        volume={currentVolume}
        playbackRate={currentSpeed}
        progressInterval={80}
        // onReady={() => {alert("onReady")}}
        // onEnded={() => {alert("onEnded")}}
        onPause={() => {
          // const peaksInstance = (globalThis as any).peaksInstance
          if (globalThis.peaksInstance) {
            globalThis.peaksInstance.player.pause();
          }

        }}
        onPlay={() => {
          //  const peaksInstance = (globalThis as any).peaksInstance
          if (globalThis.peaksInstance) {
            globalThis.peaksInstance.player.play();
          }
        }}
        onSeek={(currentSecond) => {

          const currentTime = performance.now()

          if (currentTime - globalThis.latestSeekedTime! < 100) {

            if (typeof globalThis.currentTime === 'number'
              && Math.abs(globalThis.currentTime - currentSecond) > 1) {

            }


          } else if (globalThis.peaksInstance) {
            globalThis.peaksInstance.player.seek(currentSecond);
            globalThis.currentTime = currentSecond
          }
          globalThis.latestSeekedTime = performance.now()
        }}
        // onStart={() => {}}
        onProgress={(e) => {
          setPlayer((prev: any) => ({...prev, currentTime: e.playedSeconds}))
        }}
      />
    </>
  );
}
